<template>
  <section
    id="pricing-plan"
  >
    <!-- title text and contact button -->
    <div class="text-center mb-3">
      <h1 class="mt-3 mb-1">
        {{ $t('Our Subscriptions') }}
      </h1>
      <p class="mb-2">
        {{ $t('Need several licences for your academy, club or team? Contact us for bulk prices.') }}
      </p>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        @click="openEnquiry('TOUR')"
      >
        {{ $t('Contact us for multiple licences purchase') }}
      </b-button>
    </div>
    <!--/ title text and contact button -->

    <!-- pricing plan cards -->
    <b-row class="pricing-card">
      <b-col
        md="12"
        lg="4"
        class="mx-auto"
      >
        <b-row>
          <b-col>
            <b-card
              align="center"
            >
              <h3>{{ $t('Free') }}</h3>
              <b-card-text>{{ $t('Get all the data you need') }}</b-card-text>

              <!-- plan benefit -->
              <b-list-group class="list-group-circle text-left">
                <b-list-group-item
                  v-for="feature in features.free"
                  :key="feature.label"
                  :class="feature.variant && `text-${feature.variant}`"
                >
                  {{ $t(feature.label) }}
                </b-list-group-item>
              </b-list-group>
              <!--/ plan benefit -->

            </b-card>
          </b-col>
        </b-row>
      </b-col>
      <b-col
        md="12"
        lg="4"
        class="mx-auto"
      >
        <b-row>
          <b-col>
            <b-card
              class="popular-success"
              align="center"
            >

              <h3>{{ $t('Premium (Performance)') }}</h3>
              <b-card-text>{{ $t('Get all the data you need') }}</b-card-text>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                block
                class="my-2"
                variant="success"
                @click="openEnquiry('PERFORMANCE')"
              >
                {{ $t('Contact us') }}
              </b-button>

              <p class="text-left font-weight-bold mb-0">
                {{ $t('Everything in Free, plus:') }}
              </p>
              <b-list-group class="list-group-circle text-left">
                <b-list-group-item
                  v-for="feature in features.performance"
                  :key="feature.label"
                  :class="feature.variant && `text-${feature.variant}`"
                >
                  {{ $t(feature.label) }}
                </b-list-group-item>
              </b-list-group>

            </b-card>
          </b-col>
        </b-row>
      </b-col>
      <b-col
        md="12"
        lg="4"
        class="mx-auto"
      >
        <b-row>
          <b-col>
            <b-card
              class="popular"
              align="center"
            >
              <h3>{{ $t('Premium (Tour)') }}</h3>
              <b-card-text>{{ $t('Get all the data you need') }}</b-card-text>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                block
                class="my-2"
                variant="primary"
                @click="openEnquiry('TOUR')"
              >
                {{ $t('Contact us') }}
              </b-button>

              <p class="text-left font-weight-bold mb-0">
                {{ $t('Everything in Performance, plus:') }}
              </p>
              <b-list-group class="list-group-circle text-left">
                <b-list-group-item
                  v-for="feature in features.tour"
                  :key="feature.label"
                  :class="feature.variant && `text-${feature.variant}`"
                >
                  {{ $t(feature.label) }}
                </b-list-group-item>
              </b-list-group>

            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- multiple licences enquiry -->
    <b-modal
      id="licence-enquiry"
      :title="$t('Contact us for multiple licences purchase')"
      centered
      hide-footer
    >
      <b-form @submit.prevent="sendEnquiry">
        <b-form-group
          :label="$t('Plan')"
          label-for="enquiry-plan"
        >
          <b-form-radio-group
            id="enquiry-plan"
            v-model="enquiry.plan"
            :options="planOptions"
            :button-variant="enquiry.plan === 'TOUR' ? 'outline-primary' : 'outline-success'"
            buttons
            class="d-flex plan-buttons"
          />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group
              :label="$t('First Name')"
              label-for="enquiry-firstname"
            >
              <b-form-input
                id="enquiry-firstname"
                v-model="enquiry.firstname"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('Last Name')"
              label-for="enquiry-name"
            >
              <b-form-input
                id="enquiry-name"
                v-model="enquiry.name"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group
          :label="$t('Email')"
          label-for="enquiry-mail"
        >
          <b-form-input
            id="enquiry-mail"
            v-model="enquiry.mail"
            type="email"
            required
          />
        </b-form-group>
        <b-form-group
          :label="$t('Company / Academy')"
          label-for="enquiry-company"
        >
          <b-form-input
            id="enquiry-company"
            v-model="enquiry.company"
            required
          />
        </b-form-group>
        <b-form-group
          :label="$t('Number of licences')"
          label-for="enquiry-count"
        >
          <b-form-spinbutton
            id="enquiry-count"
            v-model="enquiry.count"
            min="1"
            max="10000"
          />
        </b-form-group>
        <b-form-group
          :label="$t('Message')"
          label-for="enquiry-message"
        >
          <b-form-textarea
            id="enquiry-message"
            v-model="enquiry.message"
            rows="6"
            required
          />
        </b-form-group>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="$bvModal.hide('licence-enquiry')"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="sending"
          >
            {{ $t('Send') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </section>
</template>

<script>
import axios from '@axios'
import {
  BRow, BCol, BCard, BCardText, BListGroup, BListGroupItem, BButton, BFormSpinbutton,
  BModal, BForm, BFormGroup, BFormInput, BFormTextarea, BFormRadioGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getUserData } from '@/auth/utils'

// The pre-filled message follows the browser language, not the dashboard's
// selected one (which always starts as English).
const browserLocale = () => {
  const aliases = { ja: 'jp', zh: 'cn' }
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase()
  return aliases[lang] || lang
}

export default {
  components: {
    BButton,
    BCardText,
    BListGroup,
    BListGroupItem,
    BRow,
    BCol,
    BCard,
    BFormSpinbutton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      sending: false,
      enquiry: {},
      // Mirrors the app's plan comparison (spidergolf-rn premium-features.js)
      // and the AI limits per plan (spidergolf-nodejs app/utils/ai-quota.js).
      // The Spider AI lines are left out until AI is released; their
      // translations are already in the locale files.
      features: {
        free: [
          { label: 'Flash data entry' },
          { label: '39K courses available' },
          { label: '50+ Stats' },
          { label: 'Digital Scorecard' },
          { label: 'Spider Coach' },
          { label: 'Community feed and friends' },
        ],
        performance: [
          { label: 'Multi Stats: filter by course, tee and conditions', variant: 'success' },
          { label: 'Training', variant: 'success' },
          { label: 'Community rankings and friend profiles', variant: 'success' },
          { label: 'Live Rounds', variant: 'success' },
          { label: 'Putt Rounds', variant: 'success' },
        ],
        tour: [
          { label: 'Full Rounds: shot by shot, 400+ stats', variant: 'primary' },
          { label: 'Strokes Gained Benchmarks', variant: 'primary' },
          { label: 'Bag with club distances', variant: 'primary' },
          { label: 'Spider Caddie: preview every hole', variant: 'primary' },
          { label: 'Player Dashboards', variant: 'primary' },
          { label: 'Spider Report (PDF export)', variant: 'primary' },
        ],
      },
    }
  },
  computed: {
    planOptions() {
      return [
        { text: this.$t('Premium (Tour)'), value: 'TOUR' },
        { text: this.$t('Premium (Performance)'), value: 'PERFORMANCE' },
      ]
    },
  },
  methods: {
    openEnquiry(plan) {
      const user = getUserData() || {}
      const locale = browserLocale()
      const messageKey = 'licence_enquiry_message'
      this.enquiry = {
        plan,
        firstname: user.firstname || '',
        name: user.name || '',
        mail: user.mail || '',
        company: '',
        count: 2,
        message: this.$te(messageKey, locale) ? this.$t(messageKey, locale) : this.$t(messageKey, 'en'),
      }
      this.$bvModal.show('licence-enquiry')
    },
    sendEnquiry() {
      this.sending = true
      axios.post('/api/subscription/enquiry/', this.enquiry)
        .then(() => {
          this.$bvModal.hide('licence-enquiry')
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('Request sent'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('Thank you, our team will get back to you shortly.'),
            },
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('Request failed'),
              icon: 'SlashIcon',
              variant: 'danger',
              text: this.$t("We couldn't process your request. Please contact us at support@spidergolf.fr"),
            },
          })
        })
        .finally(() => {
          this.sending = false
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-pricing.scss';
.popular-success{
  border: 1px solid #25cfa9;
}
.plan-buttons .btn {
  flex: 1 1 0;
}
</style>
